import { Suspense, useEffect, useMemo, useState } from 'react';
import { exportExcel } from "../../../utils/exportExcel";
import { FiltroBandeja, FiltroBandejaCompleto } from '../models/filtroBandeja';
import * as yup from 'yup'
import validaRut from '../../../utils/validaRut';
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup'
import { useColumns } from '../utils/columns';
import { useLocation, useNavigate } from 'react-router-dom';
import Breadcrumb from '../../../components/ui/materialui/breadcrumb';
import Tabla from '../../../components/ui/materialui/serverSideTable';
import TableHeader from '../../../components/ui/materialui/tableHeader';
import { useDataFetch } from '../../../hooks/useDataFetch2';
import ConfirmationModal from '../../../components/ui/materialui/confirmationModal';
import InputNumber from '../../../components/inputs/yup/materialui/inputNumber';
import InputRut from '../../../components/inputs/yup/materialui/inputRut';
import InputText from '../../../components/inputs/yup/materialui/inputText';
import { Box, Container, Grid2, Paper, Typography } from '@mui/material';
import InputSelect from '../../../components/inputs/yup/materialui/inputSelect';
import MButton from '../../../components/inputs/yup/materialui/mButton';
import SummarizeTwoToneIcon from '@mui/icons-material/SummarizeTwoTone';
import CustomAccordion from '../../../components/ui/materialui/customAccordion';
import FloatingButton from '../../../components/ui/materialui/floatingButton';
import FilterListIcon from '@mui/icons-material/FilterList';
import { TablaBasicFilter } from '../../../utils/tableBasicFilter';
import { EnumEstadoMutuo, TipoFuncionalidad } from '../../../utils/enums';
import { useColumnsGarantias } from '../../detalleMutuo/utils/columnsGarantias';
import { MutuoSeleccionado } from '../models/MutuoSeleccionado';
import { ActivacionMasivaProgress, UseBandejaMutuos } from '../hooks/useBandejaMutuos';
import { ModalResultados } from '../../../components/ui/materialui/modalResultados';
import { useColumnsResults } from '../utils/columnsResults';
import { useColumnsResultsExcel } from '../utils/columnasResultsExcel';
import { PermissionGuard } from '../../../components/auth/ProtectedRoute';

const INITIAL_PROGRESS: ActivacionMasivaProgress = {
    total: 0,
    procesados: 0,
    exitosos: 0,
    fallidos: 0,
};

function BandejaMutuos() {

    const validationSchema: yup.ObjectSchema<FiltroBandeja> = yup.object().shape({

        numeroSolicitud: yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .nullable(),

        rut: yup.string().default('').nonNullable().test('valida-rut', 'RUT inválido', function (value) {
            return validaRut.rutFormat(value);
        }).max(12, 'Máximo 12 caractéres'),

        nombre: yup.string().default('').nonNullable().max(60, 'Máximo 60 caractéres'),

        estadoMutuo: yup.string().required("Filtro requerido"),
        origen: yup.string().notRequired()
    })

    const { activarMutuos } = UseBandejaMutuos();

    const {
        register,
        formState: { errors, isDirty },
        handleSubmit,
        reset,
        setValue,
        clearErrors,
        control,
    } = useForm<FiltroBandeja>({ resolver: yupResolver(validationSchema) })

    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {
        },
    })
    const [urlApiBandeja, setUrlApiBandeja] = useState<string>(`siscred-api-sani/v1/mutuos-sani/bandeja`)
    const [filtros, setFiltros] = useState<FiltroBandejaCompleto | TablaBasicFilter>({
        numeroSolicitud: null,
        rut: null,
        nombre: null,
        estadoMutuo: '',
        sort: null,
        order: null,
        take: null,
        page: null,
        search: null
    })
    const [call, setCall] = useState<boolean>(false);
    const [callFilter, setCallFilter] = useState<boolean>(false);
    const [showModalResult, setShowModalResult] = useState<boolean>(false);
    const [isActivacionMasivaRunning, setIsActivacionMasivaRunning] = useState<boolean>(false);
    const [activacionProgress, setActivacionProgress] = useState<ActivacionMasivaProgress>(INITIAL_PROGRESS);

    const controlTabla = useDataFetch(
        urlApiBandeja,
        new URLSearchParams('filters.status=ACTIVA'),
        true,
    )

    const [mutuosToActivate, setMutuosToActivate] = useState<MutuoSeleccionado[]>([]);
    const [resultadosActivacionMutuos, setResultadosActivacionMutuos] = useState<MutuoSeleccionado[]>([]);

    const { COLUMNS, contextMenuRender } = useColumns({ control, filtros, controlTabla, mutuosToActivate, setMutuosToActivate, refresh: () => { setCallFilter(true); } });

    const columns = useMemo(() => COLUMNS, [control, filtros, controlTabla]);

    const { COLUMNS: COLUMNS_RESULT } = useColumnsResults();
    const { COLUMNSEXCEL } = useColumnsResultsExcel();

    const location = useLocation();
    const { estado } = location.state || {};
    const navigate = useNavigate();

    let filtrosGuardados: FiltroBandejaCompleto | null | undefined = estado;

    const onSubmit = (data: FiltroBandeja) => filtrar(data);

    const alertActivacionMasiva = () => {
        setModalConfirmacion({
            show: true,
            title: 'Activación Masiva',
            message: '¿Estás seguro de ejecutar una activación masiva?',
            metodo: handleActivarMutuos
        });
    };

    const handleActivarMutuos = async () => {
        setIsActivacionMasivaRunning(true);
        setShowModalResult(true);

        try {
            const resultadosActivacionMutuos = await activarMutuos(mutuosToActivate, {
                concurrency: 4,
                onInit: (resultadosIniciales, progress) => {
                    setResultadosActivacionMutuos(resultadosIniciales);
                    setActivacionProgress(progress);
                },
                onItemProcessed: (_mutuo, resultadosActualizados, progress) => {
                    setResultadosActivacionMutuos(resultadosActualizados);
                    setActivacionProgress(progress);
                },
                onComplete: (resultadosFinales, progress) => {
                    setResultadosActivacionMutuos(resultadosFinales);
                    setActivacionProgress(progress);
                },
            });

            setResultadosActivacionMutuos(resultadosActivacionMutuos);
        } finally {
            setIsActivacionMasivaRunning(false);
        }
    };

    useEffect(() => {
        //solo se llama si no tiene filtros guardados
        //cuando se entra por primera vez
        if (!filtrosGuardados)
            controlTabla.fetchData();
        else
            setFiltros(filtrosGuardados);

    }, [])

    useEffect(() => {
        setValue('origen', 'MUTUARIA')
        setValue('estadoMutuo', 'POR_INGRESAR')
    }, [setValue])

    useEffect(() => {
        //Lógica de filtros cuando vuelve del detalle del mutuo    
        if (filtrosGuardados) {
            if (filtrosGuardados.numeroSolicitud) {
                controlTabla.manejarCambiosFiltros(
                    'filters.numero_solicitud',
                    filtrosGuardados.numeroSolicitud.toString()
                );
                setValue('numeroSolicitud', filtrosGuardados.numeroSolicitud);
            }

            if (filtrosGuardados.rut) {
                controlTabla.manejarCambiosFiltros(
                    'filters.rut_deudor',
                    filtrosGuardados.rut.toString()
                );
                setValue('rut', filtrosGuardados.rut);
            }

            if (filtrosGuardados.nombre) {
                controlTabla.manejarCambiosFiltros(
                    'filters.nombre_deudor',
                    filtrosGuardados.nombre.toString()
                );
                setValue('nombre', filtrosGuardados.nombre);
            }

            if (filtrosGuardados.estadoMutuo) {
                controlTabla.manejarCambiosFiltros(
                    'filters.estado_mutuo',
                    filtrosGuardados.estadoMutuo.toString()
                );
                setValue('estadoMutuo', 'INGRESADO');
            }

            if (filtrosGuardados.order && filtrosGuardados.sort) {
                controlTabla.manejarCambiosFiltros(
                    'sort',
                    filtrosGuardados.sort.toString()
                );

                controlTabla.manejarCambiosFiltros(
                    'order',
                    filtrosGuardados.order.toString()
                );
            }

            if (filtrosGuardados.search) {
                controlTabla.manejarCambiosFiltros(
                    'search',
                    filtrosGuardados.search.toString()
                );
                controlTabla.manejarCambiosFiltros(
                    'filters.search',
                    filtrosGuardados.search.toString()
                );
            }

            setUrlApiBandeja(`siscred-api-bandeja-mutuos/v1/mutuos/bandeja`);
            setCall(true);
        }

    }, [filtrosGuardados, navigate, location.pathname]);

    useEffect(() => {
        //llamado de api cuando, viene del detalle del mutuo
        if (call) {
            controlTabla.fetchData();
            setCall(false);
        }
    }, [call]);

    function filtrar(data: FiltroBandeja) {
        // se llenan los filtros para filtrar al endpoint,
        // pero también para guardarlos para utilizarlos a la vuelta

        if (data.numeroSolicitud) {
            controlTabla.manejarCambiosFiltros(
                'filters.numero_solicitud',
                data.numeroSolicitud.toString(),
            );

            setFiltros((prev) => ({
                ...prev,
                numeroSolicitud: data.numeroSolicitud
            }));
        } else {
            controlTabla.manejarCambiosFiltros('filters.numero_solicitud', '');

            setFiltros((prev) => ({
                ...prev,
                numeroSolicitud: null
            }));
        }
        if (data.rut) {
            controlTabla.manejarCambiosFiltros(
                'filters.rut_deudor',
                data.rut.toString(),
            );

            setFiltros((prev) => ({
                ...prev,
                rut: data.rut
            }));
        } else {
            controlTabla.manejarCambiosFiltros('filters.rut_deudor', '');

            setFiltros((prev) => ({
                ...prev,
                rut: ''
            }));
        }
        if (data.nombre) {
            controlTabla.manejarCambiosFiltros(
                'filters.nombre_deudor',
                data.nombre.toString(),
            );

            setFiltros((prev) => ({
                ...prev,
                nombre: data.nombre
            }));
        } else {
            controlTabla.manejarCambiosFiltros('filters.nombre_deudor', '');

            setFiltros((prev) => ({
                ...prev,
                nombre: ''
            }));
        }
        if (data.estadoMutuo) {
            controlTabla.manejarCambiosFiltros(
                'filters.estado_mutuo',
                data.estadoMutuo,
            )
            setFiltros((prev) => ({
                ...prev,
                estadoMutuo: data.estadoMutuo
            }));
        } else {
            controlTabla.manejarCambiosFiltros(
                'filters.estado_mutuo',
                '',
            )
            setFiltros((prev) => ({
                ...prev,
                estadoMutuo: ''
            }));
        }

        if (data.estadoMutuo == "POR_INGRESAR") {
            setUrlApiBandeja(`siscred-api-sani/v1/mutuos-sani/bandeja`);
        }

        if (data.estadoMutuo === EnumEstadoMutuo.Ingresado || data.estadoMutuo === EnumEstadoMutuo.Aprobado) {
            setUrlApiBandeja(`siscred-api-bandeja-mutuos/v1/mutuos/bandeja`);
        }

        setCallFilter(true);
    }

    useEffect(() => {
        if (callFilter && !call) {
            controlTabla.fetchData();
            setCallFilter(false);
        }
    }, [callFilter]);

    useEffect(() => {
        if (controlTabla.loading) setMutuosToActivate([]);
    }, [controlTabla.loading]);

    useEffect(() => {
        // cuando se hace f5 o se recarga la page manualmente, se limpian los filtros.

        const handleBeforeUnload = () => {
            filtrosGuardados = null;
            navigate(location.pathname, { replace: true, state: {} });
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const breadcrumbItems = [
        { label: 'Inicio', url: '/' },
        { label: 'Bandeja mutuos', active: true },
    ]

    return (
        <>
            <Suspense>

                <ConfirmationModal
                    onHide={() =>
                        setModalConfirmacion({ ...modalConfirmacion, show: false })
                    }
                    onConfirm={modalConfirmacion.metodo}
                    title={modalConfirmacion.title}
                    show={modalConfirmacion.show}
                    message={modalConfirmacion.message}
                />

                <ModalResultados
                    open={showModalResult}
                    onClose={() => {
                        if (isActivacionMasivaRunning) return;
                        setCallFilter(true);
                        setShowModalResult(false);
                    }}
                    title='Resultados de Activación Masiva'
                    data={resultadosActivacionMutuos}
                    columns={COLUMNS_RESULT}
                    columnsExcel={COLUMNSEXCEL}
                    excelFileName='RESULTADOS-ACTIVACION-MASIVA'
                    isProcessing={isActivacionMasivaRunning}
                    progress={activacionProgress}
                />

                <Breadcrumb items={breadcrumbItems} />

                <Box sx={{ mt: 2 }}>
                    <Paper elevation={3} sx={{ p: 3 }}>
                        <CustomAccordion
                            title='Filtros'
                            expandedDefault
                        >
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <Grid2 container spacing={2}>
                                    <Grid2 size={4}>
                                        <InputNumber name="numeroSolicitud" control={control} error={errors.numeroSolicitud} label="N° solicitud" />
                                    </Grid2>
                                    <Grid2 size={4}>
                                        <InputRut name="rut" control={control} label="RUT" />
                                        <Typography color="error">{errors.rut?.message}</Typography>
                                    </Grid2>
                                    <Grid2 size={4}>
                                        <InputText name="nombre" control={control} label="Nombre" />
                                        <Typography color="error">{errors.nombre?.message}</Typography>
                                    </Grid2>
                                </Grid2>

                                <Grid2 container spacing={2} sx={{ marginTop: 1 }}>
                                    <Grid2 size={4}>
                                        <InputSelect
                                            name="origen"
                                            label="Origen"
                                            control={control}
                                            disabled
                                            options={[
                                                { value: "MUTUARIA", label: "Mutuaria" },
                                                { value: "INMOBILIARIA", label: "Inmobiliaria" }
                                            ]}
                                        />
                                    </Grid2>
                                    <Grid2 size={4}>
                                        <InputSelect
                                            name="estadoMutuo"
                                            control={control}
                                            options={[
                                                { value: "POR_INGRESAR", label: "Por ingresar" },
                                                { value: "INGRESADO", label: "Ingresado" },
                                                { value: "APROBADO", label: "Aprobado" }
                                            ]}
                                            label="Estado"
                                            onChange={(value) => setValue("estadoMutuo", value ?? "")}
                                            error={errors.estadoMutuo}
                                        />
                                    </Grid2>
                                    <PermissionGuard permission={TipoFuncionalidad.Bandeja_ver}>
                                        <Grid2 size={4} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                            <MButton type="submit" variant="contained" color="primary" startIcon={<FilterListIcon />}>
                                                Filtrar
                                            </MButton>
                                        </Grid2>
                                    </PermissionGuard>
                                </Grid2>
                            </form>
                        </CustomAccordion>
                        <Box sx={{ mt: 4 }}>
                            <Suspense>
                                <Tabla
                                    columns={columns}
                                    control={controlTabla}
                                    url={urlApiBandeja}
                                    callApi={false}
                                    setFiltros={setFiltros}
                                    titulo={<TableHeader
                                        title=""
                                        control={controlTabla}
                                        setFiltros={setFiltros}
                                        searchFilter={filtrosGuardados?.search}
                                        button={
                                            <Grid2 container spacing={1} sx={{ justifyContent: "flex-end", textAlign: "right" }}>
                                                {(filtros as FiltroBandeja).estadoMutuo === "APROBADO" && (
                                                    <MButton
                                                        type="button"
                                                        variant='contained'
                                                        color='primary'
                                                        disabled={mutuosToActivate.length ? false : true}
                                                        onClick={alertActivacionMasiva}
                                                    >
                                                        ({mutuosToActivate.length}) Activar Mutuos
                                                    </MButton>
                                                )}
                                            </Grid2>
                                        }
                                        buttonExport={
                                            <>
                                                <PermissionGuard permission={TipoFuncionalidad.Transversal_exportar}>
                                                    <MButton
                                                        type="button"
                                                        variant="outlined"
                                                        onClick={() => exportExcel(controlTabla.data, columns, "BANDEJA-MUTUOS")}
                                                        startIcon={<SummarizeTwoToneIcon />}
                                                    >
                                                        Exportar
                                                    </MButton>
                                                </PermissionGuard>
                                                </>
                                        }
                                    />}
                                                contextMenuRender={contextMenuRender}
                                />
                                            </Suspense>
                                    </Box>
                    </Paper>
                        </Box>
                    </Suspense>
                </>
                );
}

                export default BandejaMutuos;
